import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { InterviewRecordFields, InterviewRecordItemSpec, InterviewSchedule } from '../api/client';
import { useBeforeUnloadWarning } from '../hooks/useFormDraft';
import {
  buildRecordFields,
  isMulti,
  NEXT_FOLLOW_DATE_KEY,
  NEXT_FOLLOW_KEY,
  NEXT_FOLLOW_NEEDED,
  recordableSchedules,
  scheduleLabel,
  selectableOptions,
  toDateTimeLocal,
  toggleMultiValue,
  validateRecordForm,
  type RecordForm,
  type RecordFormError,
} from '../utils/interviewRecords';

interface Props {
  items: InterviewRecordItemSpec[];
  initial: RecordForm;
  /** 修正のとき、保存済みの値（使わなくなった選択肢を選んだまま出すため） */
  previous?: RecordForm;
  submitLabel: string;
  /**
   * 結び付ける面談予定の候補（新しい記録のときだけ）。null なら選ぶ欄を出さない
   * （面談予定の一覧の API が無い古い BE。予定なしで記録を作る）
   */
  schedules?: InterviewSchedule[] | null;
  /** 送る。interviewId は選んだ面談予定（予定なしなら null）。BE の項目ごとのエラーは serverErrors で受け取る */
  onSubmit: (fields: InterviewRecordFields, interviewId: string | null) => Promise<void>;
  onCancel: () => void;
  serverErrors?: Record<string, string[]> | null;
  submitError?: string | null;
}

/**
 * 面談記録の入力（I01〜I13。課題 #71 / BE PR #143）。項目は options API から組み立てる。
 *
 * 記録は要配慮個人情報のため、書きかけを端末（localStorage）には残さない。
 * 代わりに、書きかけのまま画面を離れようとしたら確かめる。
 */
export default function InterviewRecordForm({
  items,
  initial,
  previous,
  submitLabel,
  schedules = null,
  onSubmit,
  onCancel,
  serverErrors,
  submitError,
}: Props) {
  const { t } = useTranslation();
  const [form, setForm] = useState<RecordForm>(initial);
  const [errors, setErrors] = useState<RecordFormError[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [interviewId, setInterviewId] = useState('');
  const choices = useMemo(() => (schedules ? recordableSchedules(schedules) : []), [schedules]);
  /** BE の項目ごとのエラーを受け取った後に直した項目（その項目の BE のエラーは出さない） */
  const [editedSinceServer, setEditedSinceServer] = useState<Set<string>>(new Set());
  useEffect(() => setEditedSinceServer(new Set()), [serverErrors]);
  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initial), [form, initial]);
  useBeforeUnloadWarning(dirty && !submitting);

  const set = (key: string, value: string | string[]) => {
    // 直した項目の案内は消す（次回予定日は、次回フォロー要否を変えたときも消す）
    const touched = key === NEXT_FOLLOW_KEY ? [key, NEXT_FOLLOW_DATE_KEY] : [key];
    setErrors((prev) => prev.filter((e) => !touched.includes(e.field)));
    setEditedSinceServer((prev) => new Set([...prev, ...touched]));
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      // 次回フォローが「要」でなくなったら、次回予定日を消す（「不要」のときは入れられない）
      if (key === NEXT_FOLLOW_KEY && value !== NEXT_FOLLOW_NEEDED) next[NEXT_FOLLOW_DATE_KEY] = '';
      return next;
    });
  };

  const messagesFor = (key: string): string[] => [
    ...errors
      .filter((e) => e.field === key)
      .map((e) => t(`healthcare.records.error.${e.code}`, e.params ?? {})),
    ...(editedSinceServer.has(key) ? [] : serverErrors?.[key] ?? []),
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const found = validateRecordForm(items, form);
    setErrors(found);
    if (found.length > 0) return;
    setSubmitting(true);
    try {
      await onSubmit(buildRecordFields(items, form), interviewId || null);
    } finally {
      setSubmitting(false);
    }
  };

  const hasErrors =
    errors.length > 0 || Object.keys(serverErrors ?? {}).some((key) => !editedSinceServer.has(key));

  return (
    <form className="healthcare-form interview-record-form" onSubmit={handleSubmit} noValidate data-testid="interview-record-form">
      {schedules && (
        <div className="healthcare-form-group" data-testid="record-field-interview">
          <label htmlFor="record-interview">{t('healthcare.records.schedule')}</label>
          <select
            id="record-interview"
            value={interviewId}
            onChange={(e) => {
              const id = e.target.value;
              setInterviewId(id);
              // 予定を選んだら、実施日時を予定の日時にしておく（違えば直せる）
              const chosen = choices.find((c) => c.id === id);
              if (chosen) set('recorded_at', toDateTimeLocal(new Date(chosen.scheduled_at)));
            }}
          >
            <option value="">{t('healthcare.records.scheduleNone')}</option>
            {choices.map((c) => (
              <option key={c.id} value={c.id}>
                {scheduleLabel(c)}
              </option>
            ))}
          </select>
          <p className="healthcare-form-hint">{t('healthcare.records.scheduleHint')}</p>
        </div>
      )}
      {items.map((item) => {
        const id = `record-${item.key}`;
        const value = form[item.key];
        const messages = messagesFor(item.key);
        const prev = previous?.[item.key];
        const prevList = Array.isArray(prev) ? prev : prev ? [prev] : [];
        const label = (
          <>
            {item.label}
            {item.required && <span className="interview-record-required">{t('healthcare.records.required')}</span>}
          </>
        );
        let control: React.ReactNode;
        if (isMulti(item) || item.kind === 'single' || item.kind === 'level') {
          const options = selectableOptions(item, prevList);
          const multi = isMulti(item);
          control = (
            <fieldset className="interview-record-choices" aria-describedby={messages.length ? `${id}-error` : undefined}>
              <legend>{label}</legend>
              {options.map((o) => {
                const checked = multi ? (value as string[]).includes(o.code) : value === o.code;
                return (
                  <label key={o.code}>
                    <input
                      type={multi ? 'checkbox' : 'radio'}
                      name={id}
                      value={o.code}
                      checked={checked}
                      onChange={(e) =>
                        multi
                          ? set(item.key, toggleMultiValue(item, value as string[], o.code, e.target.checked))
                          : set(item.key, o.code)
                      }
                    />
                    {o.label}
                    {o.disabled && <span className="interview-record-disabled">{t('healthcare.records.disabledOption')}</span>}
                  </label>
                );
              })}
              {/* 任意の 1 つ選ぶ項目は、選ばない（空）に戻せるようにする */}
              {!multi && !item.required && (
                <label>
                  <input type="radio" name={id} value="" checked={value === ''} onChange={() => set(item.key, '')} />
                  {t('healthcare.records.notSelected')}
                </label>
              )}
            </fieldset>
          );
        } else if (item.kind === 'datetime' || item.kind === 'date') {
          const disabled = item.key === NEXT_FOLLOW_DATE_KEY && form[NEXT_FOLLOW_KEY] !== NEXT_FOLLOW_NEEDED;
          control = (
            <>
              <label htmlFor={id}>{label}</label>
              <input
                id={id}
                type={item.kind === 'datetime' ? 'datetime-local' : 'date'}
                value={value as string}
                disabled={disabled}
                onChange={(e) => set(item.key, e.target.value)}
              />
            </>
          );
        } else {
          const text = (value as string) ?? '';
          const max = item.max_length ?? undefined;
          control = (
            <>
              <label htmlFor={id}>{label}</label>
              <textarea
                id={id}
                rows={4}
                className="healthcare-textarea"
                value={text}
                maxLength={max}
                onChange={(e) => set(item.key, e.target.value)}
              />
              {max && (
                <p className="healthcare-form-hint" data-testid="record-notes-remaining">
                  {t('healthcare.records.remaining', { count: Math.max(0, max - [...text].length) })}
                </p>
              )}
            </>
          );
        }
        return (
          <div key={item.key} className="healthcare-form-group" data-testid={`record-field-${item.key}`}>
            {control}
            {item.note && <p className="healthcare-form-hint">{item.note}</p>}
            {messages.length > 0 && (
              <p id={`${id}-error`} className="healthcare-form-error" role="alert">
                {messages.join(' ')}
              </p>
            )}
          </div>
        );
      })}
      {hasErrors && <p className="healthcare-form-error">{t('healthcare.records.fixErrors')}</p>}
      {submitError && (
        <p className="healthcare-form-error" role="alert">
          {submitError}
        </p>
      )}
      <div className="interview-record-actions">
        <button type="submit" className="healthcare-btn-primary" disabled={submitting}>
          {submitting ? t('common.loading') : submitLabel}
        </button>
        <button type="button" className="healthcare-btn-secondary" onClick={onCancel} disabled={submitting}>
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}
